// The free agents beside the lineup (B-316, DIRECTION U21): the Players
// screen's pool, compact — search, the position chips, and Add or Claim on
// every row; no strip of picks over it (B-487). The whole screen, with its
// sorts, its other filters and its deeper columns, is a tap away at the panel's
// head.
//
// Its own search and positions, kept by the league (UX-39), apart from the
// Players screen's: a filter set beside the lineup is not one the full pool
// should open on.

import { useEffect, useMemo, useState } from 'react';
import {
  failureOf,
  valueOf,
  PlayersQuery,
  type PlayerRowView,
  type PositionFilterView,
} from '@fsp/domain';
import { useLocation } from 'react-router-dom';
import { usePlayers, useReaderZone } from '../../data/hooks';
import { useLeagueSource } from '../../data/leagueSource';
import { leagueIdFromPath } from '../../data/route';
import { EmptyState, GameLabel, PositionPill, ProTeamCode, StatusBadge } from '../../kit';
import { Button, Chips, TextField } from '../../kit/ui';
import { useSettled } from '../../kit/viewport';
import { useRemembered } from '../../shell/tabMemory';
import { num } from '../shared/notComputed';
import { Proj, ProjectionWhy } from '../shared/Projection';
import { fmtWhen, type RosterMove } from '../shared/RosterMoveSheet';
import type { PlayerCardData } from '../shared/PlayerCard';
import '../../styles/screens/players.css';
import { fallbackChoices, samePositions } from '../shared/positions';
import { useTools } from '../../data/tools';

function actionLabel(r: PlayerRowView): string {
  if (r.availability.kind === 'waivers' && r.availability.myClaimId) return 'Claimed';
  return r.action === 'claim' ? 'Claim' : 'Add';
}

export function FreeAgentsPanel({
  onOpen,
  onMove,
}: {
  onOpen: (card: PlayerCardData) => void;
  onMove: (move: RosterMove) => void;
}) {
  const leagueKey = leagueIdFromPath(useLocation().pathname) ?? 'none';
  const [positions, setPositions] = useRemembered<string[]>(`side:${leagueKey}:positions`, []);
  const [q, setQ] = useRemembered(`side:${leagueKey}:q`, '');
  const real = useLeagueSource() === 'api';
  const tools = useTools();
  // A real league asks the server as you type, so it waits for a pause.
  const settledQ = useSettled(q, 150);
  const zone = useReaderZone();
  // BY THIS WEEK'S PROJECTION, the one figure its rows show: beside the
  // lineup it answers who could help this week. The pool's own default is
  // points a game (B-365), which the Players page shows.
  const query = useMemo(
    () =>
      PlayersQuery.parse({
        availability: 'free-agents',
        positions,
        q: real ? settledQ : q,
        // By points a game in a league that keeps no projections (§15c).
        sort: tools.projections ? 'projection' : 'average',
        limit: real ? 50 : 100,
      }),
    [positions, q, settledQ, real, tools.projections],
  );
  const pool = usePlayers(query);
  const page = valueOf(pool.snapshot);
  const failure = failureOf(pool.snapshot);
  // THE LAST PAGE'S CHOICES, kept while the next reads: a new query has no
  // page, and the Flex went from the chips until it had one.
  const [held, setHeld] = useState<readonly PositionFilterView[]>([]);
  const heldKey = JSON.stringify(page?.filters ?? []);
  useEffect(() => {
    const offered = JSON.parse(heldKey) as PositionFilterView[];
    if (offered.length > 0) setHeld(offered);
  }, [heldKey]);
  const choices = held.length > 0 ? held : fallbackChoices();
  const chosen = choices.find((f) => samePositions(f.positions, positions)) ?? null;
  // SEVERAL POSITIONS PICKED before the chips were one at a time are every
  // position, rather than a list narrowed by chips none of which is lit.
  const offeredKey = (page?.filters ?? []).map((f) => f.positions.join('+')).join(',');
  useEffect(() => {
    const offered = offeredKey === '' ? [] : offeredKey.split(',').map((f) => f.split('+'));
    if (
      offered.length > 0 &&
      positions.length > 0 &&
      !offered.some((f) => samePositions(f, positions))
    )
      setPositions([]);
  }, [offeredKey, positions, setPositions]);

  const act = (r: PlayerRowView) => {
    if (r.action !== 'add' && r.action !== 'claim') return;
    onMove({
      kind: r.action,
      playerId: r.player.id,
      claimId: r.availability.kind === 'waivers' ? r.availability.myClaimId : null,
    });
  };
  const open = (r: PlayerRowView) =>
    onOpen({
      player: r.player,
      game: r.game,
      projection: r.projection,
      basis: r.basis,
      points: null,
      statLine: null,
      action:
        r.action === 'add' || r.action === 'claim'
          ? { label: actionLabel(r), onClick: () => act(r) }
          : null,
    });

  return (
    <div className="fsp-side-list">
      <div className="fsp-side-filters">
        <TextField
          label="Search free agents"
          hideLabel
          placeholder="Search free agents"
          icon="search"
          value={q}
          onChange={setQ}
        />
        {/* THE LINEUP'S CHOICES, ONE AT A TIME (B-511): a Flex among them. */}
        <Chips
          label="Positions"
          clearable
          options={choices.map((f) => ({ value: f.id, label: f.label, hint: f.name }))}
          value={chosen === null ? [] : [chosen.id]}
          onChange={(v) => setPositions([...(choices.find((f) => f.id === v[0])?.positions ?? [])])}
        />
      </div>
      {page === null ? (
        <EmptyState>{failure?.message ?? 'Loading…'}</EmptyState>
      ) : page.items.length === 0 ? (
        <EmptyState>Nobody matches. Try fewer filters.</EmptyState>
      ) : (
        <div role="list" aria-label="Free agents">
          {page.items.map((r) => (
            <div
              key={r.player.id}
              role="listitem"
              className="fsp-row fsp-row-clickable fsp-side-player"
              // THE NAME IS THE BUTTON; THE ROW IS A BIGGER TARGET FOR IT (B-275).
              data-ux-convenience=""
              onClick={() => open(r)}
            >
              <PositionPill pos={r.player.positions[0]!} size="sm" />
              <div className="fsp-side-who">
                <div className="fsp-name">
                  <button
                    type="button"
                    className="fsp-linklike fsp-name-text fsp-name-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      open(r);
                    }}
                  >
                    {r.player.name}
                  </button>
                  <ProTeamCode code={r.player.proTeam} />
                  <StatusBadge player={r.player} />
                </div>
                <div className="fsp-sub fsp-side-sub">
                  <GameLabel game={r.game} />
                  {r.availability.kind === 'waivers'
                    ? zone !== null
                      ? ` · on waivers until ${fmtWhen(r.availability.clearsAt, zone)}`
                      : ' · on waivers'
                    : null}
                </div>
                {/* Why the row offers nothing, in the server's words (B-147). */}
                {r.action === 'none' && r.reason !== null ? (
                  <div className="fsp-sub fsp-muted fsp-ui-wrap-text">{r.reason}</div>
                ) : null}
              </div>
              <div className="fsp-right fsp-num fsp-side-num">
                {/* Labeled, as every projection is (B-295): with no column
                    head over it, a bare 14.2 read as a score. */}
                {tools.projections ? (
                  <Proj>
                    <ProjectionWhy player={r.player} projection={r.projection} basis={r.basis}>
                      {num(r.projection)}
                    </ProjectionWhy>
                  </Proj>
                ) : (
                  <span className="fsp-proj">
                    {num(r.seasonAvg)}
                    <span className="fsp-proj-tag">a game</span>
                  </span>
                )}
              </div>
              {/* Keeps the row's own tap off the action's (data-ux-stop: not a control). */}
              <div data-ux-stop="" onClick={(e) => e.stopPropagation()}>
                {r.action === 'add' || r.action === 'claim' ? (
                  <Button
                    size="xs"
                    full
                    variant={
                      r.availability.kind === 'waivers' && r.availability.myClaimId
                        ? 'secondary'
                        : r.action === 'claim'
                          ? 'caution-soft'
                          : 'soft'
                    }
                    onClick={() => act(r)}
                  >
                    {actionLabel(r)}
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      {page !== null && (pool.more !== null || pool.loadingMore || pool.moreFailure !== null) ? (
        <div className="fsp-side-more">
          {pool.moreFailure !== null ? (
            <span className="fsp-sub fsp-ui-wrap-text">{pool.moreFailure}</span>
          ) : null}
          <Button
            size="xs"
            variant="quiet"
            loading={pool.loadingMore}
            disabled={pool.more === null && !pool.loadingMore}
            onClick={() => pool.more?.()}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  );
}
